import React, { useState, useRef, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  SparklesIcon,
  XMarkIcon,
  ArrowPathIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

const EXAMPLE_PROMPTS = [
  "Show me voters over 55 who have voted at least 3 times",
  "Highlight Democrats in blue and Republicans in red",
  "Show a heatmap of voter density for people under 35",
  "Find independents who haven't voted in the last 2 elections",
  "Color code by age groups: under 30, 30-50, and over 50",
  "Show high-frequency voters within 1 mile of polling locations"
];

export default function NaturalLanguageBar({
  onSubmit,
  isLoading = false,
  currentSpec = null,
  onClear,
  disabled = false
}) {
  const [query, setQuery] = useState('');
  const [showExamples, setShowExamples] = useState(false);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const examplesRef = useRef(null);

  // Load history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('election_nl_history');
    if (saved) {
      try {
        setHistory(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load history:', e);
      }
    }
  }, []);

  // Close examples when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (examplesRef.current && !examplesRef.current.contains(event.target)) {
        setShowExamples(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isLoading || disabled) return;

    const trimmedQuery = query.trim();

    // Add to history
    const newHistory = [trimmedQuery, ...history.filter(h => h !== trimmedQuery)].slice(0, 10);
    setHistory(newHistory);
    localStorage.setItem('election_nl_history', JSON.stringify(newHistory));

    await onSubmit(trimmedQuery);
  };

  const handleExampleClick = (example) => {
    setQuery(example);
    setShowExamples(false);
    inputRef.current?.focus();
  };

  const handleClear = () => {
    setQuery('');
    if (onClear) onClear();
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full">
      {/* Main input bar */}
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex items-center bg-white border border-gray-300 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
          {/* AI Icon */}
          <div className="pl-3 pr-2">
            <SparklesIcon className={`h-5 w-5 ${isLoading ? 'text-blue-500 animate-pulse' : 'text-gray-400'}`} />
          </div>

          {/* Input */}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setShowExamples(true)}
            placeholder="Describe how you want to visualize the voter data..."
            className="flex-1 py-3 px-2 border-0 focus:ring-0 focus:outline-none text-gray-900 placeholder-gray-500"
            disabled={isLoading || disabled}
          />

          {/* Clear button */}
          {(query || currentSpec) && (
            <button
              type="button"
              onClick={handleClear}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={!query.trim() || isLoading || disabled}
            className={`mr-2 px-4 py-2 rounded-md text-sm font-medium transition-colors
              ${!query.trim() || isLoading || disabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
          >
            {isLoading ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <MagnifyingGlassIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </form>

      {/* Current visualization summary */}
      {currentSpec && currentSpec.summary && (
        <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-blue-800">
              <SparklesIcon className="h-4 w-4 mr-2" />
              <span className="font-medium">Active: </span>
              <span className="ml-1">{currentSpec.summary}</span>
            </div>
            {currentSpec.filters && currentSpec.filters.length > 0 && (
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                {currentSpec.filters.length} filter{currentSpec.filters.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Examples dropdown */}
      {showExamples && !isLoading && (
        <div
          ref={examplesRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          {/* Recent queries */}
          {history.length > 0 && (
            <div className="border-b border-gray-100">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Recent
              </div>
              {history.slice(0, 3).map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => handleExampleClick(item)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="truncate">{item}</span>
                </button>
              ))}
            </div>
          )}

          {/* Example prompts */}
          <div>
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center">
              <LightBulbIcon className="h-3 w-3 mr-1" />
              Examples
            </div>
            {EXAMPLE_PROMPTS.map((example, idx) => (
              <button
                key={idx}
                onClick={() => handleExampleClick(example)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <SparklesIcon className="h-4 w-4 mr-2 text-blue-400" />
                <span>{example}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
